import { useId, type ReactElement, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { clsx } from "clsx";
import { layout } from "@tokens/index";
import { Text } from "@primitives/Text";
import { pressHandlers } from "@primitives/press";
import { useViewport } from "@primitives/viewport";
import styles from "./SettingsRow.module.css";

/**
 * SettingsRow: one line of a `SettingsGroup`.
 *
 * Left: an optional leading glyph, the label (body weight), and an optional
 * muted description capped at the prose measure. Right: the variant's
 * trailing content, vertically centered.
 *
 *  - `control`     a form control. Pass `children` as a render function to
 *                  receive the label and description ids for
 *                  `aria-labelledby` / `aria-describedby`. `wideControl`
 *                  drops the control under the label on `compact`.
 *  - `action`      a `Button`.
 *  - `navigation`  the whole row is a button with a chevron and the shared
 *                  press physics.
 *  - `readonly`    a value on the right, no interaction.
 */

export type SettingsRowVariant = "control" | "action" | "navigation" | "readonly";

export interface SettingsRowControlIds {
    readonly labelId: string;
    readonly descriptionId: string | undefined;
}

interface SettingsRowBaseProps {
    /** Base for the label and description ids. Generated when omitted. */
    readonly id?: string;
    readonly label: string;
    readonly description?: ReactNode;
    /** A 16px icon or an `Avatar` in a 32px slot before the text. */
    readonly leading?: ReactNode;
    readonly testId?: string;
}

interface ControlRowProps extends SettingsRowBaseProps {
    readonly variant?: "control";
    /** The control is wide (segmented) and stacks under the label on `compact`. */
    readonly wideControl?: boolean;
    readonly children: ReactNode | ((ids: SettingsRowControlIds) => ReactNode);
}

interface ActionRowProps extends SettingsRowBaseProps {
    readonly variant: "action";
    readonly children: ReactNode;
}

interface NavigationRowProps extends SettingsRowBaseProps {
    readonly variant: "navigation";
    readonly onSelect: () => void;
    readonly disabled?: boolean;
}

interface ReadonlyRowProps extends SettingsRowBaseProps {
    readonly variant: "readonly";
    readonly value?: ReactNode;
}

export type SettingsRowProps =
    | ControlRowProps
    | ActionRowProps
    | NavigationRowProps
    | ReadonlyRowProps;

export function SettingsRow(props: SettingsRowProps): ReactElement {
    const { id, label, description, leading, testId } = props;
    const { isCompact } = useViewport();
    const generatedId = useId();
    const baseId = id ?? generatedId;
    const labelId = `${baseId}-label`;
    const descriptionId = description !== undefined ? `${baseId}-description` : undefined;
    const variant = props.variant ?? "control";
    const wideControl =
        props.variant === undefined || props.variant === "control"
            ? props.wideControl === true
            : false;
    const stacked = wideControl && isCompact;

    const text = (
        <div className={styles.text}>
            <Text as="span" id={labelId} role="body">
                {label}
            </Text>
            {description !== undefined && (
                <Text
                    as="span"
                    id={`${baseId}-description`}
                    role="bodySm"
                    tone="muted"
                    style={{ maxWidth: layout.proseMaxWidth }}
                >
                    {description}
                </Text>
            )}
        </div>
    );

    const leadingSlot =
        leading !== undefined ? (
            <span className={styles.leading} aria-hidden="true">
                {leading}
            </span>
        ) : null;

    const dataProps = {
        "data-settings-row": variant,
        "data-leading": leading !== undefined ? "" : undefined,
        "data-stacked": stacked ? "" : undefined,
        "data-testid": testId,
    };

    if (props.variant === "navigation") {
        const inert = props.disabled === true;
        return (
            <button
                type="button"
                className={clsx(styles.row, styles.nav)}
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                disabled={inert}
                onClick={props.onSelect}
                {...pressHandlers<HTMLButtonElement>({}, inert)}
                {...dataProps}
            >
                {leadingSlot}
                {text}
                <span className={styles.trailing}>
                    <ChevronRight
                        size={16}
                        strokeWidth={2.25}
                        aria-hidden="true"
                        className={styles.chevron}
                    />
                </span>
            </button>
        );
    }

    let trailing: ReactNode;
    if (props.variant === "readonly") {
        trailing =
            typeof props.value === "string" ? (
                <Text role="body" tone="muted" truncate>
                    {props.value}
                </Text>
            ) : (
                props.value
            );
    } else if (props.variant === "action") {
        trailing = props.children;
    } else {
        trailing =
            typeof props.children === "function"
                ? props.children({ labelId, descriptionId })
                : props.children;
    }

    return (
        <div className={styles.row} {...dataProps}>
            {leadingSlot}
            {text}
            {trailing !== undefined && trailing !== null && (
                <div className={styles.trailing}>{trailing}</div>
            )}
        </div>
    );
}
